import { format, isToday, isSameDay } from 'date-fns';
import { useRecords } from '../../../hooks/useRecords';
import { useBabies } from '../../../hooks/useBabies';
import { useState, useEffect } from 'react';

interface ActivityTimelineProps {
  date: Date;
}

interface Activity {
  id: string;
  type: 'feed' | 'diaper' | 'sleep' | 'growth' | 'custom';
  time: string;
  duration?: string;
  notes?: string;
  amount?: string;
  details?: string;
  category?: string;
  title?: string;
}

interface CustomActivity {
  id: string;
  title: string;
  category: string;
  time: string;
  description: string;
  completed: boolean;
  date: string;
}

export default function ActivityTimeline({ date }: ActivityTimelineProps) {
  const { activeBaby } = useBabies();
  const { feedingRecords, sleepRecords, diaperRecords, growthRecords } = useRecords(activeBaby?.id ?? null);
  const [customActivities, setCustomActivities] = useState<CustomActivity[]>([]);

  // Cargar actividades personalizadas del localStorage usando la fecha prop
  useEffect(() => {
    if (activeBaby) {
      const loadCustomActivities = () => {
        const dateString = date.toISOString().split('T')[0];
        const storageKey = `activities_${activeBaby.id}_${dateString}`;
        console.log('Cargando actividades desde:', storageKey);
        console.log('Fecha seleccionada:', dateString);
        const stored = localStorage.getItem(storageKey);
        console.log('Datos encontrados:', stored);
        if (stored) {
          const activities = JSON.parse(stored);
          console.log('Actividades parseadas:', activities);
          setCustomActivities(activities);
        } else {
          setCustomActivities([]);
        }
      };

      loadCustomActivities();

      // Escuchar eventos de actualización de actividades
      const handleActivitiesUpdate = () => {
        console.log('Evento activitiesUpdated recibido');
        loadCustomActivities();
      };

      window.addEventListener('activitiesUpdated', handleActivitiesUpdate);

      return () => {
        window.removeEventListener('activitiesUpdated', handleActivitiesUpdate);
      };
    }
  }, [activeBaby, date]); // Agregamos date como dependencia

  // Debug: mostrar actividades personalizadas
  useEffect(() => {
    console.log('Custom activities actualizadas:', customActivities);
  }, [customActivities]);

  // Función helper para obtener nombre de categoría - MOVIDA ANTES DE getRealActivities
  const getCategoryName = (category: string) => {
    switch (category) {
      case 'juego':
        return 'Juego';
      case 'cuidado':
        return 'Cuidado';
      case 'desarrollo':
        return 'Desarrollo';
      case 'salud':
        return 'Salud';
      case 'otro':
        return 'Otro';
      default:
        return 'Actividad';
    }
  };

  // Cuánto duró, calculado del inicio y el fin. Antes se leía un campo
  // `duration_minutes` que no existe en la base, así que la duración no
  // se mostraba nunca.
  const duracion = (inicio: string, fin?: string): string | undefined => {
    if (!fin) return undefined;
    const minutos = Math.round(
      (new Date(fin).getTime() - new Date(inicio).getTime()) / 60000
    );
    if (minutos <= 0) return undefined;
    if (minutos < 60) return `${minutos} min`;
    const horas = Math.floor(minutos / 60);
    const resto = minutos % 60;
    return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`;
  };

  // Convertir registros reales a actividades y filtrar por la fecha seleccionada
  const getRealActivities = (): Activity[] => {
    const activities: Activity[] = [];

    // Agregar registros de alimentación de la fecha seleccionada
    //
    // Se filtra y se muestra por la hora REAL del evento (`start_time`),
    // no por `created_at`, que es cuándo se guardó la fila. Si un padre
    // anota a las 11 de la noche la toma de las 6 de la tarde, tiene
    // que aparecer a las 6 — y en el día que corresponde.
    feedingRecords
      .filter(record => isSameDay(new Date(record.start_time), date))
      .forEach(record => {
        activities.push({
          id: `feed-${record.id}`,
          type: 'feed',
          time: format(new Date(record.start_time), 'HH:mm'),
          duration: duracion(record.start_time, record.end_time),
          amount: record.amount ? `${record.amount}ml` : undefined,
          notes: record.notes || undefined,
          // El campo se llama `type`. Antes se leía `feeding_type`, que
          // no existe: la comparación daba siempre falso y TODA toma
          // salía rotulada como "Biberón", incluso las de pecho.
          details: record.type === 'breast' ? 'Lactancia materna'
                 : record.type === 'bottle' ? 'Biberón'
                 : 'Sólidos'
        });
      });

    // Agregar registros de pañales de la fecha seleccionada
    diaperRecords
      .filter(record => isSameDay(new Date(record.time), date))
      .forEach(record => {
        activities.push({
          id: `diaper-${record.id}`,
          type: 'diaper',
          time: format(new Date(record.time), 'HH:mm'),
          notes: record.notes || undefined,
          // Mismo caso: el campo es `type`, no `diaper_type`. Todos los
          // pañales aparecían como "mixto".
          details: record.type === 'wet' ? 'Pañal mojado'
                 : record.type === 'dirty' ? 'Pañal sucio'
                 : 'Pañal mixto'
        });
      });

    // Agregar registros de sueño de la fecha seleccionada
    sleepRecords
      .filter(record => isSameDay(new Date(record.start_time), date))
      .forEach(record => {
        activities.push({
          id: `sleep-${record.id}`,
          type: 'sleep',
          time: format(new Date(record.start_time), 'HH:mm'),
          duration: duracion(record.start_time, record.end_time),
          notes: record.notes || undefined
        });
      });

    // Agregar registros de crecimiento de la fecha seleccionada
    growthRecords
      .filter(record => isSameDay(new Date(record.recorded_at), date))
      .forEach(record => {
        // Los campos son `weight` y `height`. Con `weight_kg` y
        // `height_cm` la ficha mostraba "Peso: undefinedkg".
        const partes = [
          record.weight ? `Peso: ${record.weight} kg` : null,
          record.height ? `Talla: ${record.height} cm` : null
        ].filter(Boolean);

        activities.push({
          id: `growth-${record.id}`,
          type: 'growth',
          time: format(new Date(record.recorded_at), 'HH:mm'),
          details: partes.join(', ') || 'Medición registrada'
        });
      });

    // Agregar actividades personalizadas de la fecha seleccionada
    console.log('Agregando actividades personalizadas al timeline:', customActivities.length);
    customActivities.forEach(activity => {
      console.log('Agregando actividad:', activity);
      activities.push({
        id: `custom-${activity.id}`,
        type: 'custom',
        time: activity.time,
        title: activity.title,
        category: activity.category,
        notes: activity.description || undefined,
        details: getCategoryName(activity.category)
      });
    });

    // Ordenar por tiempo (más reciente primero)
    const sortedActivities = activities.sort((a, b) => b.time.localeCompare(a.time));
    console.log('Total de actividades en timeline:', sortedActivities.length);
    return sortedActivities;
  };

  const activities = getRealActivities();

  const getActivityIcon = (type: string, category?: string) => {
    switch (type) {
      case 'feed':
        return 'ri-heart-fill';
      case 'diaper':
        return 'ri-drop-fill';
      case 'sleep':
        return 'ri-moon-fill';
      case 'growth':
        return 'ri-ruler-fill';
      case 'custom':
        switch (category) {
          case 'juego':
            return 'ri-gamepad-fill';
          case 'cuidado':
            return 'ri-heart-fill';
          case 'desarrollo':
            return 'ri-book-fill';
          case 'salud':
            return 'ri-health-book-fill';
          case 'otro':
            return 'ri-star-fill';
          default:
            return 'ri-calendar-fill';
        }
      default:
        return 'ri-time-fill';
    }
  };

  const getActivityColor = (type: string, category?: string) => {
    switch (type) {
      case 'feed':
        return 'bg-pink-500';
      case 'diaper':
        return 'bg-blue-500';
      case 'sleep':
        return 'bg-purple-500';
      case 'growth':
        return 'bg-green-500';
      case 'custom':
        switch (category) {
          case 'juego':
            return 'bg-blue-500';
          case 'cuidado':
            return 'bg-pink-500';
          case 'desarrollo':
            return 'bg-green-500';
          case 'salud':
            return 'bg-red-500';
          case 'otro':
            return 'bg-gray-500';
          default:
            return 'bg-teal-500';
        }
      default:
        return 'bg-gray-500';
    }
  };

  const getActivityName = (type: string, title?: string, category?: string) => {
    if (type === 'custom') {
      return title || getCategoryName(category || '');
    }
    
    switch (type) {
      case 'feed':
        return 'Alimentación';
      case 'diaper':
        return 'Cambio de Pañal';
      case 'sleep':
        return 'Sueño';
      case 'growth':
        return 'Crecimiento';
      default:
        return 'Actividad';
    }
  };

  // Si no hay bebé seleccionado
  if (!activeBaby) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Timeline del Día</h3>
        <div className="flex items-center justify-center py-8">
          <p className="text-gray-500 text-center">Selecciona un bebé para ver las actividades</p>
        </div>
      </div>
    );
  }

  // Si no hay actividades
  if (activities.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Timeline del Día</h3>
        <div className="flex flex-col items-center justify-center py-8">
          <i className="ri-calendar-line text-4xl text-gray-300 mb-2"></i>
          <p className="text-gray-500 text-center">No hay actividades registradas hoy</p>
          <p className="text-gray-400 text-sm text-center mt-1">
            Comienza registrando una actividad desde el botón "Agregar Actividad"
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
      <h3 className="text-lg font-bold text-gray-800 mb-4">
        Timeline del {isToday(date) ? 'Día' : format(date, 'dd/MM/yyyy')}
      </h3>
      
      <div className="space-y-4">
        {activities.map((activity, index) => (
          <div key={activity.id} className="flex items-start space-x-4">
            <div className="flex flex-col items-center">
              <div className={`
                w-10 h-10 rounded-full ${getActivityColor(activity.type, activity.category)} 
                flex items-center justify-center shadow-md
              `}>
                <i className={`${getActivityIcon(activity.type, activity.category)} text-white text-sm`}></i>
              </div>
              {index < activities.length - 1 && (
                <div className="w-0.5 h-8 bg-gray-200 mt-2"></div>
              )}
            </div>
            
            <div className="flex-1 pb-4">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-semibold text-gray-800 text-sm">
                  {getActivityName(activity.type, activity.title, activity.category)}
                </h4>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {activity.time}
                </span>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-2">
                {activity.duration && (
                  <span className="text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded-full">
                    {activity.duration}
                  </span>
                )}
                {activity.amount && (
                  <span className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
                    {activity.amount}
                  </span>
                )}
              </div>
              
              {activity.details && (
                <p className="text-xs text-gray-600 mb-1">{activity.details}</p>
              )}
              
              {activity.notes && (
                <p className="text-xs text-gray-500 italic">{activity.notes}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
